/**
 * «فرستاده نشد» — سیگنالی که فیلترِ واژه‌ها نگه داشت (WHOSUP-309، مایگریشن
 * 0174)، یا مدیر بعد از گزارش پنهانش کرد (0175). قرینه‌ی
 * apps/mobile/src/features/signals/HeldSignalCard.tsx.
 *
 * فقط سازنده این سیگنال را می‌بیند (RLS). نگه‌داشتنِ فیلتر: ویرایشِ متن
 * می‌فرستدش، یا اعتراض و صبر برای رأیِ مدیر. نگه‌داشتنِ مدیر: دلیل و شرطِ
 * مدیر را می‌گوید؛ ویرایش آزادش نمی‌کند، «دوباره بررسی کن» می‌کند.
 */
import React from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { signalRepo } from '@/core/data/signalRepo';
import { t } from '@/core/lib/i18n';
import { reportError } from '@/core/lib/report';
import type { Signal } from '@/core/domain/types';
import { Card, Note } from '@/ui/kit';

export function HeldSignalCard({ signal: s }: { signal: Signal }) {
  const qc = useQueryClient();
  const appeal = useMutation({
    mutationFn: () => signalRepo.appealHeld(s),
    onSuccess: () => {
      window.alert(t('filterAppealSent'));
      qc.invalidateQueries({ queryKey: ['signal', s.id] });
      qc.invalidateQueries({ queryKey: ['mine'] });
    },
    onError: (e) => { reportError('web.signal.appealHeld', e); window.alert(t('errorRetry')); },
  });
  const byAdmin = !!s.heldReason;
  const appealOpen = s.heldAppealStatus === 'open';
  const upheld = s.heldAppealStatus === 'upheld';
  // مدیر: بعد از رد هم می‌شود اصلاح کرد و دوباره خواست.
  const canAppeal = byAdmin ? !appealOpen : !appealOpen && !upheld;

  return (
    <Card>
      <h3>⛔ {t(byAdmin ? 'modHeldTitle' : 'heldTitle')}</h3>
      {byAdmin ? (
        <>
          <Note tone="brand">{t('modHeldBody', { reason: s.heldReason! })}</Note>
          {s.heldFix ? <Note>{t('modHeldFix', { fix: s.heldFix })}</Note> : null}
        </>
      ) : (
        <Note tone="brand">{t('heldBody', { words: (s.heldWords ?? []).join(', ') })}</Note>
      )}
      {appealOpen ? <Note>{t(byAdmin ? 'modHeldAppealOpen' : 'heldAppealOpen')}</Note> : null}
      {upheld ? <Note tone="brand">{t(byAdmin ? 'modHeldAppealUpheld' : 'heldAppealUpheld')}</Note> : null}
      <div className="chiprow">
        <Link className="btn" to={`/signal/${s.id}/edit`}>{t('heldEdit')}</Link>
        {canAppeal ? (
          <button type="button" className="btn" disabled={appeal.isPending}
            onClick={() => appeal.mutate()}>{t(byAdmin ? 'modHeldReview' : 'filterAppeal')}</button>
        ) : null}
      </div>
    </Card>
  );
}
